import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type FocusEvent } from "react";
import { createPortal } from "react-dom";
import { SegmentPill } from "../components/SegmentPill";
import { findTeamMember, IRONFLEET_TEAM } from "../floor/teamColors";
import { formatNoteStamp } from "../ratings";
import { PRIORITIES, contactIdLabel, familiarNames, formatPhone, initials, linkedInHref, linkedInLabel, shortContactId, toggleUserId, userIdsMatch } from "./logic";
import type { DebriefCard, DebriefFollowup, DebriefNote, DebriefPatch, LeadRating } from "./types";

export type SaveStatus = "saving" | "saved" | "offline" | "error";

function OtherContactsPill({
  siblings,
  eventKey,
  open,
  onOpen,
  onClose,
  onSelect,
}: {
  siblings: DebriefCard[];
  eventKey: string | null;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSelect: (contactId: string) => void;
}) {
  const pillRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!open || !pillRef.current) return;
    const rect = pillRef.current.getBoundingClientRect();
    const width = 300;
    const maxHeight = 240;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const top = spaceBelow < 160 ? Math.max(8, rect.top - maxHeight - 4) : rect.bottom + 4;
    setMenuStyle({ top, left, width, maxHeight, zIndex: 1700 });
  }, [open, siblings.length]);

  useEffect(() => {
    if (!open) return;
    const insideMenu = (node: EventTarget | null) => {
      if (!(node instanceof Node) || !menuRef.current) return false;
      return node === menuRef.current || menuRef.current.contains(node);
    };
    const onPointer = (event: MouseEvent) => {
      const node = event.target;
      if (node instanceof Node && pillRef.current?.contains(node)) return;
      if (insideMenu(node)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    const onScroll = (event: Event) => {
      if (insideMenu(event.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [open, onClose]);

  return (
    <div className="segment-pill-wrap">
      <button
        ref={pillRef}
        type="button"
        className={open ? "segment-pill is-open" : "segment-pill"}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? onClose() : onOpen())}
      >
        <span className="df-wide">Other account contacts</span>
        <span className="df-narrow">+{siblings.length} contacts</span>
      </button>
      {open
        ? createPortal(
            <div ref={menuRef} className="segment-pill-menu df-other-menu" role="listbox" aria-label="Other account contacts" style={menuStyle}>
              {siblings.map((person) => {
                const onShow = person.eventKey === eventKey && person.eventContactId != null;
                return (
                  <div key={person.contactId} className="df-other-card" role="option" aria-selected={false}>
                    <span className="df-other-photo" aria-hidden="true">
                      {person.photoUrl ? <img src={person.photoUrl} alt="" /> : initials(person.fullName)}
                    </span>
                    <span className="df-other-copy">
                      <strong>{person.fullName}</strong>
                      <small>{person.title}</small>
                    </span>
                    <button
                      type="button"
                      className="df-other-add"
                      aria-label={onShow ? `${person.fullName} is already on this show` : `Add ${person.fullName}`}
                      disabled={onShow}
                      onClick={() => {
                        onClose();
                        onSelect(person.contactId);
                      }}
                    >
                      +
                    </button>
                  </div>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function BusinessCardThumb({ card, onOpen }: { card: DebriefCard; onOpen: () => void }) {
  const url = card.businessCardUrl;
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  const showThumb = Boolean(url) && brokenSrc !== url;

  useEffect(() => {
    setBrokenSrc(null);
  }, [card.contactId, url]);

  return (
    <button
      type="button"
      className={showThumb ? "df-biz has-scan" : "df-biz"}
      aria-label={`Business card for ${card.fullName}`}
      onClick={onOpen}
    >
      {showThumb ? (
        <img src={url ?? undefined} alt="" referrerPolicy="no-referrer" onError={() => setBrokenSrc(url)} />
      ) : (
        <>
          <span className="df-biz-name">{card.fullName}</span>
          <span className="df-biz-meta">{card.accountName}</span>
        </>
      )}
    </button>
  );
}

function OwnerPill({
  contactId,
  ownerUserId,
  onSelect,
}: {
  contactId: string;
  ownerUserId: string | null;
  onSelect: (userId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const owner = ownerUserId ? findTeamMember(ownerUserId) : undefined;
  const label = owner?.name.split(/\s+/)[0] || "Owner";

  useEffect(() => {
    setOpen(false);
  }, [contactId]);

  useLayoutEffect(() => {
    if (!open || !pillRef.current) return;
    const rect = pillRef.current.getBoundingClientRect();
    const width = 196;
    const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const top = spaceBelow < 160 ? Math.max(8, rect.top - 280 - 4) : rect.bottom + 4;
    setMenuStyle({ top, left, width, maxHeight: 280, zIndex: 1700 });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const insideMenu = (node: EventTarget | null) => {
      if (!(node instanceof Node) || !menuRef.current) return false;
      return node === menuRef.current || menuRef.current.contains(node);
    };
    const onPointer = (event: MouseEvent) => {
      const node = event.target;
      if (node instanceof Node && pillRef.current?.contains(node)) return;
      if (insideMenu(node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setOpen(false);
    };
    const onScroll = (event: Event) => {
      if (insideMenu(event.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  return (
    <>
      <button
        ref={pillRef}
        type="button"
        className={["df-rel-pill", owner ? "on" : "", open ? "is-open" : ""].filter(Boolean).join(" ")}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={owner ? `Owner ${owner.name}` : "Owner"}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
      </button>
      {open
        ? createPortal(
            <div ref={menuRef} className="segment-pill-menu" role="listbox" aria-label="Owner" style={menuStyle}>
              <button
                type="button"
                className="segment-pill-option"
                role="option"
                aria-selected={!owner}
                onClick={() => {
                  setOpen(false);
                  onSelect(null);
                }}
              >
                No owner
              </button>
              {IRONFLEET_TEAM.map((member) => {
                const selected = Boolean(ownerUserId && userIdsMatch(ownerUserId, member.email));
                return (
                  <button
                    key={member.email}
                    type="button"
                    className="segment-pill-option"
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      setOpen(false);
                      onSelect(member.email);
                    }}
                  >
                    {member.name}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function savedLabel(status: SaveStatus, savedAt: number, now: number): string {
  if (status === "saving") return "Saving…";
  if (status === "offline") return "Offline · will sync";
  if (status === "error") return "Not saved · Retry";
  const minutes = Math.floor((now - savedAt) / 60000);
  if (minutes < 1) return "Saved · just now";
  if (minutes === 1) return "Saved · 1 min ago";
  return `Saved · ${minutes} min ago`;
}

export function ContactCard({
  card,
  siblings,
  position,
  total,
  currentUserId,
  nameByUser,
  saveStatus,
  savedAt,
  now,
  onPatch,
  onRetry,
  onAddSibling,
  onOpenAccount,
  onPhotoNav,
  headingRef,
}: {
  card: DebriefCard;
  siblings: DebriefCard[];
  position: number;
  total: number;
  currentUserId: string;
  nameByUser: Map<string, string>;
  saveStatus: SaveStatus;
  savedAt: number;
  now: number;
  onPatch: (patch: DebriefPatch, immediate: boolean) => void;
  onRetry: () => void;
  onAddSibling: (contactId: string) => void;
  onOpenAccount: (accountId: string) => void;
  onPhotoNav: (delta: -1 | 1) => void;
  headingRef: (node: HTMLHeadingElement | null) => void;
}) {
  const [copied, setCopied] = useState<"email" | "phone" | "id" | null>(null);
  const [segmentOpen, setSegmentOpen] = useState(false);
  const [othersOpen, setOthersOpen] = useState(false);
  const copyTimer = useRef<number | null>(null);
  const [notePage, setNotePage] = useState(0);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<DebriefNote | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState("");
  const [followPage, setFollowPage] = useState(0);
  const [pendingFollowId, setPendingFollowId] = useState<string | null>(null);
  const [editingFollow, setEditingFollow] = useState<DebriefFollowup | null>(null);
  const [followTitle, setFollowTitle] = useState("");
  const [followDue, setFollowDue] = useState("");
  const [composingFollow, setComposingFollow] = useState(false);
  const [followDraft, setFollowDraft] = useState("");
  const [followDraftDue, setFollowDraftDue] = useState("");
  const editTitleId = useId();
  const deleteTitleId = useId();
  const followEditTitleId = useId();
  const followDeleteTitleId = useId();
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaView, setMediaView] = useState<"photo" | "card">("photo");
  const [cardSide, setCardSide] = useState<"front" | "back">(card.businessCardFrontUrl ? "front" : "back");
  const [cardBrokenSrc, setCardBrokenSrc] = useState<string | null>(null);
  const [photoSegmentOpen, setPhotoSegmentOpen] = useState(false);
  const notes = [...card.notes].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const notePages = Math.max(1, Math.ceil(notes.length / 2));
  const visiblePage = Math.min(notePage, notePages - 1);
  const visibleNotes = notes.slice(visiblePage * 2, visiblePage * 2 + 2);
  const pendingDelete = notes.find((note) => note.id === pendingDeleteId) ?? null;
  const followups = [...card.followups].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const followPages = Math.max(1, Math.ceil(followups.length / 2));
  const visibleFollowPage = Math.min(followPage, followPages - 1);
  const visibleFollowups = followups.slice(visibleFollowPage * 2, visibleFollowPage * 2 + 2);
  const pendingFollow = followups.find((item) => item.followupId === pendingFollowId) ?? null;
  const linkedIn = linkedInHref(card.linkedinUrl);
  const fullContactId = contactIdLabel(card);
  const briefContactId = shortContactId(card);
  const emailAt = card.email?.lastIndexOf("@") ?? -1;
  const emailLocal = card.email && emailAt > 0 ? card.email.slice(0, emailAt) : card.email;
  const emailDomain = card.email && emailAt > 0 ? card.email.slice(emailAt) : "";
  const others = familiarNames(card, currentUserId, nameByUser);
  const mine = card.familiarUserIds.some((id) => userIdsMatch(id, currentUserId));

  useEffect(() => {
    return () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    };
  }, []);

  useEffect(() => {
    setNotePage(0);
    setPendingDeleteId(null);
    setEditingNote(null);
    setEditDraft("");
    setComposing(false);
    setDraft("");
    setFollowPage(0);
    setPendingFollowId(null);
    setEditingFollow(null);
    setFollowTitle("");
    setFollowDue("");
    setComposingFollow(false);
    setFollowDraft("");
    setFollowDraftDue("");
  }, [card.contactId]);

  useEffect(() => {
    setCardSide(card.businessCardFrontUrl ? "front" : "back");
    setCardBrokenSrc(null);
  }, [card.contactId, card.businessCardFrontUrl]);

  useEffect(() => {
    if (!mediaOpen) setPhotoSegmentOpen(false);
  }, [mediaOpen, card.contactId]);

  useEffect(() => {
    if (!mediaOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (photoSegmentOpen) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setMediaOpen(false);
        return;
      }
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (total < 2) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      onPhotoNav(event.key === "ArrowLeft" ? -1 : 1);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [mediaOpen, photoSegmentOpen, onPhotoNav, total]);

  useEffect(() => {
    if (!pendingDeleteId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setPendingDeleteId(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [pendingDeleteId]);

  useEffect(() => {
    if (!editingNote) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setEditingNote(null);
      setEditDraft("");
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [editingNote]);

  useEffect(() => {
    if (!pendingFollowId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setPendingFollowId(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [pendingFollowId]);

  useEffect(() => {
    if (!editingFollow) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setEditingFollow(null);
      setFollowTitle("");
      setFollowDue("");
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [editingFollow]);

  function deleteNote(id: string) {
    setPendingDeleteId(null);
    onPatch({ notes: card.notes.filter((note) => note.id !== id), reviewed: true }, true);
  }

  function openDelete(id: string) {
    setEditingNote(null);
    setEditDraft("");
    setPendingFollowId(null);
    setEditingFollow(null);
    setPendingDeleteId(id);
  }

  function openEdit(note: DebriefNote) {
    setPendingDeleteId(null);
    setPendingFollowId(null);
    setEditingFollow(null);
    setEditingNote(note);
    setEditDraft(note.body);
  }

  function closeEdit() {
    setEditingNote(null);
    setEditDraft("");
  }

  function saveEdit() {
    if (!editingNote) return;
    const body = editDraft.trim();
    if (!body || body === editingNote.body) {
      closeEdit();
      return;
    }
    onPatch(
      {
        notes: card.notes.map((note) =>
          note.id === editingNote.id
            ? {
                ...note,
                body,
                authorId: currentUserId,
                authorName: nameByUser.get(currentUserId) || "You",
                createdAt: new Date().toISOString(),
              }
            : note,
        ),
        reviewed: true,
      },
      true,
    );
    closeEdit();
  }

  function deleteFollowup(id: string) {
    setPendingFollowId(null);
    onPatch({ followups: card.followups.filter((item) => item.followupId !== id), reviewed: true }, true);
  }

  function openFollowDelete(id: string) {
    setEditingFollow(null);
    setFollowTitle("");
    setFollowDue("");
    setPendingDeleteId(null);
    setEditingNote(null);
    setPendingFollowId(id);
  }

  function openFollowEdit(item: DebriefFollowup) {
    setPendingFollowId(null);
    setPendingDeleteId(null);
    setEditingNote(null);
    setEditingFollow(item);
    setFollowTitle(item.title);
    setFollowDue(item.dueAt ?? "");
  }

  function closeFollowEdit() {
    setEditingFollow(null);
    setFollowTitle("");
    setFollowDue("");
  }

  function saveFollowEdit() {
    if (!editingFollow?.followupId) return;
    const title = followTitle.trim();
    const dueAt = followDue || null;
    if (!title || (title === editingFollow.title.trim() && dueAt === editingFollow.dueAt)) {
      closeFollowEdit();
      return;
    }
    onPatch(
      {
        followups: card.followups.map((item) =>
          item.followupId === editingFollow.followupId ? { ...item, title, dueAt } : item,
        ),
        reviewed: true,
      },
      true,
    );
    closeFollowEdit();
  }

  function saveFollowup() {
    const title = followDraft.trim();
    if (!title) return;
    const item: DebriefFollowup = {
      followupId: `local:${crypto.randomUUID()}`,
      title,
      dueAt: followDraftDue || null,
      createdAt: new Date().toISOString(),
    };
    onPatch({ followups: [item, ...card.followups], reviewed: true }, true);
    setFollowDraft("");
    setFollowDraftDue("");
    setComposingFollow(false);
    setFollowPage(0);
  }

  function formatFollowDate(dueAt: string | null): string {
    if (!dueAt) return "No date";
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dueAt);
    if (!match) return dueAt;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" }).format(date);
  }

  function saveNote() {
    const body = draft.trim();
    if (!body) return;
    const note: DebriefNote = {
      id: crypto.randomUUID(),
      body,
      authorId: currentUserId,
      authorName: nameByUser.get(currentUserId) || "You",
      createdAt: new Date().toISOString(),
    };
    onPatch({ notes: [note, ...card.notes], reviewed: true }, true);
    setDraft("");
    setComposing(false);
    setNotePage(0);
  }

  function keepFieldVisible(event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) {
    if (!window.matchMedia("(max-width: 600px)").matches) return;
    const node = event.currentTarget;
    window.setTimeout(() => node.scrollIntoView({ block: "center", inline: "nearest" }), 280);
  }

  async function copyValue(kind: "email" | "phone" | "id", value: string) {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const area = document.createElement("textarea");
      area.value = value;
      document.body.appendChild(area);
      area.select();
      document.execCommand("copy");
      area.remove();
    }
    setCopied(kind);
    if (copyTimer.current) window.clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopied(null), 1500);
  }

  const cardFront = card.businessCardFrontUrl;
  const cardBack = card.businessCardBackUrl;
  const cardPhone = card.phoneE164 ? formatPhone(card.phoneE164) : null;
  const activeScan = cardSide === "back" && cardBack ? cardBack : cardFront ?? card.businessCardUrl;
  const showCardScan = Boolean(activeScan) && cardBrokenSrc !== activeScan;
  const mediaLabel =
    mediaView === "card"
      ? `Business card for ${card.fullName}, ${position} of ${total}`
      : `Photo of ${card.fullName}, ${position} of ${total}`;

  function openMedia(view: "photo" | "card") {
    if (view === "card") setCardSide(card.businessCardFrontUrl ? "front" : "back");
    setMediaView(view);
    setMediaOpen(true);
  }

  function setRating(next: LeadRating) {
    onPatch({ rating: card.rating === next ? 0 : next, reviewed: true }, true);
  }

  function toggleId(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
  }

  return (
    <article
      className="df-card"
      role="group"
      aria-roledescription="slide"
      aria-label={`${position} of ${total}: ${card.fullName}`}
    >
      <header className="df-head">
        {card.photoUrl ? (
          <button
            type="button"
            className="df-avatar df-avatar-button"
            aria-label={`View photo of ${card.fullName}`}
            onClick={() => openMedia("photo")}
          >
            <img src={card.photoUrl} alt="" />
          </button>
        ) : (
          <div className="df-avatar" aria-hidden="true">
            {initials(card.fullName)}
          </div>
        )}
        <div className="df-id">
          <div className="df-name-row">
            <div className="df-name-copy">
              <h2 ref={headingRef} tabIndex={-1}>
                {card.fullName}
              </h2>
              {card.title ? <p className="df-title">{card.title}</p> : null}
              <p className="df-contact-id">
                <span className="df-wide">Contact ID: {fullContactId ?? "—"}</span>
                {fullContactId ? (
                  <button
                    type="button"
                    className="df-narrow df-id-copy"
                    aria-label={`Copy contact ID ${fullContactId}`}
                    onClick={() => void copyValue("id", fullContactId)}
                  >
                    {copied === "id" ? "Copied" : briefContactId}
                  </button>
                ) : (
                  <span className="df-narrow">—</span>
                )}
                <span className="df-live" aria-live="polite">
                  {copied === "id" ? "Contact ID copied" : ""}
                </span>
              </p>
            </div>
          </div>
          <div className="df-chips">
            {card.email ? (
              <span className="df-chip">
                <span className="df-email">
                  <span className="df-email-local">{emailLocal}</span>
                  {emailDomain ? <span className="df-email-domain">{emailDomain}</span> : null}
                </span>
                <button type="button" aria-label="Copy email" onClick={() => void copyValue("email", card.email ?? "")}>
                  {copied === "email" ? "✓" : "⧉"}
                </button>
                <span className="df-live" aria-live="polite">
                  {copied === "email" ? "Email copied" : ""}
                </span>
              </span>
            ) : null}
            {card.phoneE164 ? (
              <span className="df-chip">
                <a href={`tel:${card.phoneE164}`}>{formatPhone(card.phoneE164)}</a>
                <button type="button" aria-label="Copy phone" onClick={() => void copyValue("phone", card.phoneE164 ?? "")}>
                  {copied === "phone" ? "✓" : "⧉"}
                </button>
                <span className="df-live" aria-live="polite">
                  {copied === "phone" ? "Phone copied" : ""}
                </span>
              </span>
            ) : null}
            {linkedIn ? (
              <a className="df-link" href={linkedIn} target="_blank" rel="noopener noreferrer">
                {linkedInLabel(linkedIn)} ↗
              </a>
            ) : null}
          </div>
          <div className="df-company-row">
            <button type="button" className="df-company" onClick={() => onOpenAccount(card.accountId)}>
              {card.accountName}
            </button>
            <SegmentPill
              value={card.segment}
              open={segmentOpen}
              onOpen={() => {
                setOthersOpen(false);
                setSegmentOpen(true);
              }}
              onClose={() => setSegmentOpen(false)}
              onSelect={(segment) => onPatch({ segment, reviewed: true }, true)}
            />
            {siblings.length > 0 ? (
              <OtherContactsPill
                siblings={siblings}
                eventKey={card.eventKey}
                open={othersOpen}
                onOpen={() => {
                  setSegmentOpen(false);
                  setOthersOpen(true);
                }}
                onClose={() => setOthersOpen(false)}
                onSelect={onAddSibling}
              />
            ) : null}
          </div>
        </div>
        <div className="df-rate">
          <BusinessCardThumb card={card} onOpen={() => openMedia("card")} />
          <div className="df-rate-stars">
            <span className="df-label df-rating-label">Lead rating</span>
            <div className="df-stars" role="radiogroup" aria-label="Lead rating">
              {([1, 2, 3, 4, 5] as LeadRating[]).map((star) => (
                <button
                  key={star}
                  type="button"
                  role="radio"
                  aria-checked={card.rating === star}
                  aria-label={`${star} star${star === 1 ? "" : "s"}`}
                  className={star <= card.rating ? "on" : ""}
                  onClick={() => setRating(star)}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div className="df-familiar">
            <button
              type="button"
              className={mine ? "df-familiar-name on" : "df-familiar-name"}
              aria-pressed={mine}
              onClick={() =>
                onPatch({ familiarUserIds: toggleUserId(card.familiarUserIds, currentUserId), reviewed: true }, true)
              }
            >
              Familiar Face
            </button>
            {others.length > 0 ? (
              <p>
                {mine ? "also met" : "Met"} {others.join(", ")}
              </p>
            ) : null}
          </div>
          <div className="df-rel">
            <button
              type="button"
              className={card.priorityFollowup ? "df-rel-pill on" : "df-rel-pill"}
              aria-pressed={Boolean(card.priorityFollowup)}
              onClick={() => onPatch({ priorityFollowup: !card.priorityFollowup, reviewed: true }, true)}
            >
              Priority
            </button>
            <button
              type="button"
              className={card.keyContact ? "df-rel-pill on" : "df-rel-pill"}
              aria-pressed={Boolean(card.keyContact)}
              aria-label="Key contact"
              onClick={() => onPatch({ keyContact: !card.keyContact, reviewed: true }, true)}
            >
              Key Contact
            </button>
            <OwnerPill
              contactId={card.contactId}
              ownerUserId={card.ownerUserId ?? null}
              onSelect={(ownerUserId) => onPatch({ ownerUserId, reviewed: true }, true)}
            />
          </div>
        </div>
      </header>

      <div className="df-work">
        <section className="df-notes" aria-label="Notes">
          <div className="df-label-row df-notes-label">
            <span className="df-label">Notes</span>
            <button
              type="button"
              className="df-note-add"
              aria-label="Add note"
              aria-expanded={composing}
              onClick={() => setComposing((open) => !open)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="M8 3.25v9.5M3.25 8h9.5" />
              </svg>
            </button>
            {notes.length > 2 ? (
              <div className="df-note-tools">
                <div className="df-note-nav" aria-label="Notes pages">
                  <button
                    type="button"
                    aria-label="Previous notes"
                    disabled={visiblePage === 0}
                    onClick={() => setNotePage(visiblePage - 1)}
                  >
                    ‹
                  </button>
                  <span>
                    {visiblePage + 1} / {notePages}
                  </span>
                  <button
                    type="button"
                    aria-label="Next notes"
                    disabled={visiblePage >= notePages - 1}
                    onClick={() => setNotePage(visiblePage + 1)}
                  >
                    ›
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          {composing ? (
            <form
              className="df-note-form"
              onSubmit={(event) => {
                event.preventDefault();
                saveNote();
              }}
            >
              <textarea
                maxLength={5000}
                rows={3}
                value={draft}
                autoFocus
                placeholder="Write a note"
                aria-label="New note"
                onFocus={keepFieldVisible}
                onChange={(event) => setDraft(event.target.value)}
              />
              <div className="df-note-form-actions">
                <button type="submit" disabled={!draft.trim()}>
                  Save note
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setComposing(false);
                    setDraft("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
          <div className="df-note-grid" role="region" aria-roledescription="carousel" aria-label="Stamped notes">
            {visibleNotes.map((note) => (
              <article key={note.id} className="df-note-card">
                <header>
                  <time dateTime={note.createdAt}>{formatNoteStamp(note.createdAt)}</time>
                  <span className="df-note-who">
                    <span>{note.authorName}</span>
                    <button
                      type="button"
                      className="df-note-edit"
                      aria-label={`Edit note from ${note.authorName}`}
                      onClick={() => openEdit(note)}
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M9.1 3.2 12.8 6.9" />
                        <path d="M2.9 13.1 3.4 10.4 10.2 3.6a1.15 1.15 0 0 1 1.6 0l.6.6a1.15 1.15 0 0 1 0 1.6L5.6 12.6z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="df-note-delete"
                      aria-label={`Delete note from ${note.authorName}`}
                      onClick={() => openDelete(note.id)}
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M3.25 4.25h9.5" />
                        <path d="M6.25 4.25V3.4a.9.9 0 0 1 .9-.9h1.7a.9.9 0 0 1 .9.9v.85" />
                        <path d="M4.4 4.25l.55 8.15a.9.9 0 0 0 .9.8h4.3a.9.9 0 0 0 .9-.8l.55-8.15" />
                      </svg>
                    </button>
                  </span>
                </header>
                <p>{note.body}</p>
              </article>
            ))}
            {notes.length === 0 ? <p className="df-note-empty">No notes yet.</p> : null}
          </div>
        </section>
        <section className="df-notes" aria-label="Follow-ups">
          <div className="df-label-row df-notes-label">
            <span className="df-label">Follow-ups</span>
            <button
              type="button"
              className="df-note-add"
              aria-label="Add follow-up"
              aria-expanded={composingFollow}
              onClick={() => setComposingFollow((open) => !open)}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="M8 3.25v9.5M3.25 8h9.5" />
              </svg>
            </button>
            {followups.length > 2 ? (
              <div className="df-note-tools">
                <div className="df-note-nav" aria-label="Follow-up pages">
                  <button
                    type="button"
                    aria-label="Previous follow-ups"
                    disabled={visibleFollowPage === 0}
                    onClick={() => setFollowPage(visibleFollowPage - 1)}
                  >
                    ‹
                  </button>
                  <span>
                    {visibleFollowPage + 1} / {followPages}
                  </span>
                  <button
                    type="button"
                    aria-label="Next follow-ups"
                    disabled={visibleFollowPage >= followPages - 1}
                    onClick={() => setFollowPage(visibleFollowPage + 1)}
                  >
                    ›
                  </button>
                </div>
              </div>
            ) : null}
          </div>
          {composingFollow ? (
            <form
              className="df-note-form"
              onSubmit={(event) => {
                event.preventDefault();
                saveFollowup();
              }}
            >
              <textarea
                maxLength={2000}
                rows={3}
                value={followDraft}
                autoFocus
                placeholder="Write a follow-up"
                aria-label="New follow-up"
                onFocus={keepFieldVisible}
                onChange={(event) => setFollowDraft(event.target.value)}
              />
              <label className="df-follow-due">
                <span className="df-label">Date</span>
                <input
                  type="date"
                  value={followDraftDue}
                  aria-label="Follow-up date"
                  onFocus={keepFieldVisible}
                  onChange={(event) => setFollowDraftDue(event.target.value)}
                />
              </label>
              <div className="df-note-form-actions">
                <button type="submit" disabled={!followDraft.trim()}>
                  Save follow-up
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setComposingFollow(false);
                    setFollowDraft("");
                    setFollowDraftDue("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
          <div className="df-note-grid" role="region" aria-roledescription="carousel" aria-label="Follow-ups">
            {visibleFollowups.map((item) => (
              <article key={item.followupId ?? item.createdAt} className="df-note-card">
                <header>
                  <time dateTime={item.dueAt ?? undefined}>{formatFollowDate(item.dueAt)}</time>
                  <span className="df-note-who">
                    <button
                      type="button"
                      className="df-note-edit"
                      aria-label="Edit follow-up"
                      onClick={() => openFollowEdit(item)}
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M9.1 3.2 12.8 6.9" />
                        <path d="M2.9 13.1 3.4 10.4 10.2 3.6a1.15 1.15 0 0 1 1.6 0l.6.6a1.15 1.15 0 0 1 0 1.6L5.6 12.6z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="df-note-delete"
                      aria-label="Delete follow-up"
                      disabled={!item.followupId}
                      onClick={() => item.followupId && openFollowDelete(item.followupId)}
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M3.25 4.25h9.5" />
                        <path d="M6.25 4.25V3.4a.9.9 0 0 1 .9-.9h1.7a.9.9 0 0 1 .9.9v.85" />
                        <path d="M4.4 4.25l.55 8.15a.9.9 0 0 0 .9.8h4.3a.9.9 0 0 0 .9-.8l.55-8.15" />
                      </svg>
                    </button>
                  </span>
                </header>
                <p>{item.title}</p>
              </article>
            ))}
            {followups.length === 0 ? <p className="df-note-empty">No follow-ups yet.</p> : null}
          </div>
        </section>
      </div>

      <section className="df-block">
        <div className="df-label-row">
          <span className="df-label">Strategic priorities</span>
          <span className="df-count">
            {card.priorityIds.length} of {PRIORITIES.length}
          </span>
        </div>
        <div className="df-pills">
          {PRIORITIES.map((priority) => {
            const on = card.priorityIds.includes(priority.id);
            return (
              <button
                key={priority.id}
                type="button"
                aria-pressed={on}
                className={on ? "df-priority on" : "df-priority"}
                onClick={() => onPatch({ priorityIds: toggleId(card.priorityIds, priority.id), reviewed: true }, true)}
              >
                {on ? "✓ " : ""}
                {priority.label}
              </button>
            );
          })}
        </div>
      </section>

      <div className="df-save-row">
        <button
          type="button"
          className={`df-saved ${saveStatus}`}
          onClick={saveStatus === "error" ? onRetry : undefined}
          aria-live="polite"
        >
          {saveStatus === "saved" ? "✓ " : saveStatus === "error" ? "⚠ " : ""}
          {savedLabel(saveStatus, savedAt, now)}
        </button>
      </div>
      {pendingDelete
        ? createPortal(
            <div className="df-note-modal-backdrop" role="presentation" onClick={() => setPendingDeleteId(null)}>
              <form
                className="df-note-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={deleteTitleId}
                onClick={(event) => event.stopPropagation()}
                onSubmit={(event) => {
                  event.preventDefault();
                  deleteNote(pendingDelete.id);
                }}
              >
                <button type="button" className="df-note-modal-close" aria-label="Close" onClick={() => setPendingDeleteId(null)}>
                  ×
                </button>
                <h2 id={deleteTitleId}>Delete note</h2>
                <p className="df-note-modal-meta">
                  {pendingDelete.authorName} · {formatNoteStamp(pendingDelete.createdAt)}
                </p>
                <p className="df-note-modal-preview">{pendingDelete.body}</p>
                <p className="df-note-modal-prompt">Delete note - are you sure?</p>
                <div className="df-note-form-actions">
                  <button type="submit">Delete</button>
                  <button type="button" autoFocus onClick={() => setPendingDeleteId(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>,
            document.body,
          )
        : null}
      {pendingFollow
        ? createPortal(
            <div className="df-note-modal-backdrop" role="presentation" onClick={() => setPendingFollowId(null)}>
              <form
                className="df-note-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={followDeleteTitleId}
                onClick={(event) => event.stopPropagation()}
                onSubmit={(event) => {
                  event.preventDefault();
                  if (pendingFollow.followupId) deleteFollowup(pendingFollow.followupId);
                }}
              >
                <button type="button" className="df-note-modal-close" aria-label="Close" onClick={() => setPendingFollowId(null)}>
                  ×
                </button>
                <h2 id={followDeleteTitleId}>Delete follow-up</h2>
                <p className="df-note-modal-meta">{formatFollowDate(pendingFollow.dueAt)}</p>
                <p className="df-note-modal-preview">{pendingFollow.title}</p>
                <p className="df-note-modal-prompt">Delete follow-up - are you sure?</p>
                <div className="df-note-form-actions">
                  <button type="submit">Delete</button>
                  <button type="button" autoFocus onClick={() => setPendingFollowId(null)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>,
            document.body,
          )
        : null}
      {editingFollow
        ? createPortal(
            <div className="df-note-modal-backdrop" role="presentation" onClick={closeFollowEdit}>
              <form
                className="df-note-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={followEditTitleId}
                onClick={(event) => event.stopPropagation()}
                onSubmit={(event) => {
                  event.preventDefault();
                  saveFollowEdit();
                }}
              >
                <button type="button" className="df-note-modal-close" aria-label="Close" onClick={closeFollowEdit}>
                  ×
                </button>
                <h2 id={followEditTitleId}>Edit follow-up</h2>
                <label className="df-follow-due">
                  <span className="df-label">Date</span>
                  <input
                    type="date"
                    value={followDue}
                    aria-label="Follow-up date"
                    onFocus={keepFieldVisible}
                    onChange={(event) => setFollowDue(event.target.value)}
                  />
                </label>
                <textarea
                  maxLength={2000}
                  rows={6}
                  value={followTitle}
                  autoFocus
                  aria-label="Follow-up"
                  onFocus={keepFieldVisible}
                  onChange={(event) => setFollowTitle(event.target.value)}
                />
                <div className="df-note-form-actions">
                  <button
                    type="submit"
                    disabled={
                      !followTitle.trim() ||
                      (followTitle.trim() === editingFollow.title.trim() && (followDue || null) === editingFollow.dueAt)
                    }
                  >
                    Save follow-up
                  </button>
                  <button type="button" onClick={closeFollowEdit}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>,
            document.body,
          )
        : null}
      {editingNote
        ? createPortal(
            <div className="df-note-modal-backdrop" role="presentation" onClick={closeEdit}>
              <form
                className="df-note-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={editTitleId}
                onClick={(event) => event.stopPropagation()}
                onSubmit={(event) => {
                  event.preventDefault();
                  saveEdit();
                }}
              >
                <button type="button" className="df-note-modal-close" aria-label="Close" onClick={closeEdit}>
                  ×
                </button>
                <h2 id={editTitleId}>Edit note</h2>
                <p className="df-note-modal-meta">
                  {editingNote.authorName} · {formatNoteStamp(editingNote.createdAt)}
                </p>
                <textarea
                  maxLength={5000}
                  rows={6}
                  value={editDraft}
                  autoFocus
                  aria-label="Note"
                  onFocus={keepFieldVisible}
                  onChange={(event) => setEditDraft(event.target.value)}
                />
                <div className="df-note-form-actions">
                  <button type="submit" disabled={!editDraft.trim() || editDraft.trim() === editingNote.body}>
                    Save note
                  </button>
                  <button type="button" onClick={closeEdit}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>,
            document.body,
          )
        : null}
      {mediaOpen
        ? createPortal(
            <div className="df-photo-backdrop" role="presentation" onClick={() => setMediaOpen(false)}>
              <div
                className="df-photo-modal"
                role="dialog"
                aria-modal="true"
                aria-label={mediaLabel}
                onClick={(event) => event.stopPropagation()}
              >
                <div className="df-photo-switch" role="tablist" aria-label="Photo or business card">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mediaView === "photo"}
                    className={mediaView === "photo" ? "on" : ""}
                    onClick={() => setMediaView("photo")}
                  >
                    Photo
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mediaView === "card"}
                    className={mediaView === "card" ? "on" : ""}
                    onClick={() => setMediaView("card")}
                  >
                    Business Card
                  </button>
                </div>
                {mediaView === "card" && cardFront && cardBack ? (
                  <div className="df-photo-sides" role="group" aria-label="Card side">
                    <button type="button" aria-pressed={cardSide !== "back"} onClick={() => setCardSide("front")}>
                      Front
                    </button>
                    <button type="button" aria-pressed={cardSide === "back"} onClick={() => setCardSide("back")}>
                      Back
                    </button>
                  </div>
                ) : null}
                <button type="button" className="df-photo-close" aria-label="Close" onClick={() => setMediaOpen(false)}>
                  ×
                </button>
                {mediaView === "photo" ? (
                  card.photoUrl ? (
                    <img src={card.photoUrl} alt={`Photo of ${card.fullName}`} />
                  ) : (
                    <div className="df-photo-fallback">{initials(card.fullName)}</div>
                  )
                ) : (
                  <div className={cardFront && cardBack ? "df-photo-card-frame has-sides" : "df-photo-card-frame"}>
                    {showCardScan ? (
                      <img
                        src={activeScan ?? undefined}
                        alt={`Business card for ${card.fullName}`}
                        referrerPolicy="no-referrer"
                        onError={() => setCardBrokenSrc(activeScan)}
                      />
                    ) : (
                      <div className="df-biz-sheet">
                        <p className="df-biz-sheet-name">{card.fullName}</p>
                        <p className="df-biz-sheet-title">{card.title}</p>
                        <p className="df-biz-sheet-company">{card.accountName}</p>
                        <div className="df-biz-sheet-lines">
                          {card.email ? <p>{card.email}</p> : null}
                          {cardPhone ? <p>{cardPhone}</p> : null}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="df-photo-caption">
                  <p className="df-photo-name">{card.fullName}</p>
                  <p className="df-photo-company">{card.accountName}</p>
                  <SegmentPill
                    value={card.segment}
                    open={photoSegmentOpen}
                    menuZIndex={1900}
                    menuAnchor="above"
                    onOpen={() => setPhotoSegmentOpen(true)}
                    onClose={() => setPhotoSegmentOpen(false)}
                    onSelect={(segment) => onPatch({ segment, reviewed: true }, true)}
                  />
                  <div className="df-stars" role="radiogroup" aria-label="Lead rating">
                    {([1, 2, 3, 4, 5] as LeadRating[]).map((star) => (
                      <button
                        key={star}
                        type="button"
                        role="radio"
                        aria-checked={card.rating === star}
                        aria-label={`${star} star${star === 1 ? "" : "s"}`}
                        className={star <= card.rating ? "on" : ""}
                        onClick={() => setRating(star)}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  <div className="df-photo-actions">
                    {total > 1 ? (
                      <button type="button" className="df-photo-nav" aria-label="Previous contact" onClick={() => onPhotoNav(-1)}>
                        ‹
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className={mine ? "df-familiar-name on" : "df-familiar-name"}
                      aria-pressed={mine}
                      onClick={() =>
                        onPatch({ familiarUserIds: toggleUserId(card.familiarUserIds, currentUserId), reviewed: true }, true)
                      }
                    >
                      Familiar Face
                    </button>
                    {total > 1 ? (
                      <button type="button" className="df-photo-nav" aria-label="Next contact" onClick={() => onPhotoNav(1)}>
                        ›
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </article>
  );
}
